import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";

import { trimText } from "@/lib/utils";
import {
  buildSearchWithTrail,
  inferNavigationTrailFromPathname,
  mergeNavigationTrailState,
  navigationTrailLocationKey,
  navigationTrailsEqual,
  readTrailFromHistoryState,
  readTrailFromLocation,
  readTrailFromSearch,
  resolveNavigationTrailLabel,
  stripNavigationTrailFromSearch,
  syncNavigationTrailOnLocationChange,
  type NavigationTrailEntry,
  type NavigationTrailPendingAction,
} from "@/lib/navigationTrail";

interface NavigationTrailContextValue {
  trail: NavigationTrailEntry[];
  currentLabel: string;
  resetNavigationTrail: () => void;
  navigateToTrailEntry: (index: number) => void;
}

const NavigationTrailContext = createContext<NavigationTrailContextValue | null>(null);

function normalizeSearchString(search: string): string {
  if (search.length === 0) return "";
  return search.startsWith("?") ? search : `?${search}`;
}

function resolveTrailForLocation(
  pathname: string,
  search: string,
  state: unknown,
  pendingAction: NavigationTrailPendingAction,
  registeredLabels: ReadonlyMap<string, string>,
): NavigationTrailEntry[] {
  const fromLocation = readTrailFromLocation(search, state, registeredLabels);
  if (fromLocation.length > 0) {
    return fromLocation;
  }
  if (pendingAction?.type === "reset") {
    return [];
  }
  return inferNavigationTrailFromPathname(pathname);
}

function maybePersistTrailInLocation(
  search: string,
  state: unknown,
  trail: NavigationTrailEntry[],
): { search: string; state: Record<string, unknown> | null } | null {
  if (trail.length === 0) {
    return null;
  }

  const builtSearch = buildSearchWithTrail(search, trail);
  const mergedState = mergeNavigationTrailState(state, trail);
  const stateTrail = readTrailFromHistoryState(state);
  const urlTrail = readTrailFromSearch(search);
  const searchMatches = normalizeSearchString(builtSearch) === normalizeSearchString(search);
  const stateMatches = navigationTrailsEqual(trail, stateTrail);
  const legacyJsonTrail = urlTrail.length > 1;

  if (searchMatches && stateMatches && !legacyJsonTrail) {
    return null;
  }

  return { search: builtSearch, state: mergedState };
}

export function NavigationTrailProvider({ children }: PropsWithChildren): JSX.Element {
  const location = useLocation();
  const navigationType = useNavigationType();
  const navigate = useNavigate();
  const [trail, setTrail] = useState<NavigationTrailEntry[]>(() =>
    resolveTrailForLocation(location.pathname, location.search, location.state, null, new Map()),
  );
  const [labelVersion, setLabelVersion] = useState(0);
  const registeredLabelsRef = useRef(new Map<string, string>());
  const pendingActionRef = useRef<NavigationTrailPendingAction>(null);
  const previousLocationRef = useRef<{ pathname: string; search: string; state: unknown } | null>(null);
  const skipInitialEffectRef = useRef(true);

  useEffect(() => {
    const { pathname, search, state } = location;

    if (skipInitialEffectRef.current) {
      skipInitialEffectRef.current = false;
      previousLocationRef.current = { pathname, search, state };
      const initialTrail = resolveTrailForLocation(
        pathname,
        search,
        state,
        null,
        registeredLabelsRef.current,
      );
      setTrail(initialTrail);
      const persisted = maybePersistTrailInLocation(search, state, initialTrail);
      if (persisted != null) {
        void navigate({ pathname, search: persisted.search }, { replace: true, state: persisted.state });
      }
      return;
    }

    const previous = previousLocationRef.current;
    previousLocationRef.current = { pathname, search, state };
    if (previous == null) return;

    const pendingAction = pendingActionRef.current;
    pendingActionRef.current = null;

    const result = syncNavigationTrailOnLocationChange({
      previousPathname: previous.pathname,
      previousSearch: previous.search,
      previousState: previous.state,
      nextPathname: pathname,
      nextSearch: search,
      nextState: state,
      navigationType,
      pendingAction,
      registeredLabels: registeredLabelsRef.current,
    });

    let nextTrail = result.trail;
    if (nextTrail.length === 0 && pendingAction?.type !== "reset") {
      nextTrail = inferNavigationTrailFromPathname(pathname);
    }

    setTrail(nextTrail);

    let nextSearch = result.search;
    let shouldUpdateSearch = result.updateSearch;
    const persisted = maybePersistTrailInLocation(search, state, nextTrail);
    if (persisted != null) {
      nextSearch = persisted.search;
      shouldUpdateSearch = true;
    }

    const nextState = persisted?.state ?? mergeNavigationTrailState(state, nextTrail);
    const stateTrail = readTrailFromHistoryState(state);
    const shouldUpdateState = !navigationTrailsEqual(nextTrail, stateTrail);

    if (shouldUpdateSearch || shouldUpdateState) {
      void navigate(
        { pathname, search: nextSearch ?? stripNavigationTrailFromSearch(search) },
        { replace: true, state: nextState },
      );
    }
  }, [location.pathname, location.search, location.state, navigationType, navigate]);

  const resetNavigationTrail = useCallback(() => {
    pendingActionRef.current = { type: "reset" };
  }, []);

  const navigateToTrailEntry = useCallback(
    (index: number) => {
      const entry = trail[index];
      if (entry == null) return;
      const trimmedTrail = trail.slice(0, index);
      pendingActionRef.current = { type: "trim", index };
      navigate(
        {
          pathname: entry.pathname,
          search: buildSearchWithTrail(entry.search, trimmedTrail),
        },
        { state: mergeNavigationTrailState(undefined, trimmedTrail) },
      );
    },
    [navigate, trail],
  );

  const currentLabel = useMemo(
    () => resolveNavigationTrailLabel(location.pathname, location.search, registeredLabelsRef.current),
    [location.pathname, location.search, labelVersion],
  );

  const registerPageLabel = useCallback((pathname: string, search: string, label: string) => {
    const trimmed = trimText(label);
    if (trimmed === "") return;
    const fullKey = navigationTrailLocationKey(pathname, search);
    if (
      registeredLabelsRef.current.get(fullKey) === trimmed &&
      registeredLabelsRef.current.get(pathname) === trimmed
    ) {
      return;
    }
    registeredLabelsRef.current.set(fullKey, trimmed);
    registeredLabelsRef.current.set(pathname, trimmed);
    setLabelVersion((version) => version + 1);
  }, []);

  const value = useMemo<NavigationTrailContextValue>(
    () => ({
      trail,
      currentLabel,
      resetNavigationTrail,
      navigateToTrailEntry,
    }),
    [trail, currentLabel, resetNavigationTrail, navigateToTrailEntry],
  );

  return (
    <NavigationTrailContext.Provider value={value}>
      <PageLabelRegistryContext.Provider value={registerPageLabel}>{children}</PageLabelRegistryContext.Provider>
    </NavigationTrailContext.Provider>
  );
}

const PageLabelRegistryContext = createContext<((pathname: string, search: string, label: string) => void) | null>(
  null,
);

/** Register the visible page title for breadcrumb labels on this route. */
export function usePageBreadcrumb(label: string): void {
  const location = useLocation();
  const registerPageLabel = useContext(PageLabelRegistryContext);

  useLayoutEffect(() => {
    if (registerPageLabel == null) return;
    registerPageLabel(location.pathname, location.search, label);
  }, [registerPageLabel, location.pathname, location.search, label]);
}

export function useNavigationTrail(): NavigationTrailContextValue {
  const context = useContext(NavigationTrailContext);
  if (context == null) {
    throw new Error("useNavigationTrail must be used within NavigationTrailProvider");
  }
  return context;
}
